import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { useConfiguration } from '../../context/configurationContext';
import { ResponsiveImage } from '../../components';
import OutsideClickHandler from '../OutsideClickHandler/OutsideClickHandler';

import css from './ComparePicker.module.css';

const optionLabel = (listing, intl) => {
  const title = listing?.attributes?.title || '';
  const price = listing?.attributes?.price;
  if (!price) {
    return title;
  }
  const formattedPrice = formatMoney(intl, price);
  return `${title} (${formattedPrice})`;
};

const CompareDropdown = props => {
  const {
    id,
    label,
    listings,
    selectedId,
    disabledId,
    onSelect,
  } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const selectedListing = listings.find(l => l?.id?.uuid === selectedId);

  const getBrandDisplayName = brandValue => {
    if (!brandValue) return 'Autres';
    const brandStr = Array.isArray(brandValue) ? brandValue[0] : brandValue;
    if (typeof brandStr !== 'string') return `${brandStr}`;
    const brandFieldConfig = config.listing.listingFields?.find(field => field.key === 'brand');
    const brandEnumOptions = brandFieldConfig?.enumOptions || [];
    const brandOption = brandEnumOptions.find(option => option.option === brandStr);
    if (brandOption) return brandOption.label;
    return brandStr
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const normalizedFilter = filter.trim().toLowerCase();
  const groupedListings = useMemo(() => {
    const byBrand = new Map();
    const matchesFilter = listing => {
      if (!normalizedFilter) return true;
      const title = listing?.attributes?.title || '';
      const shortDescription =
        listing?.attributes?.publicData?.shortdescription || listing?.attributes?.description || '';
      return (
        title.toLowerCase().includes(normalizedFilter) ||
        shortDescription.toLowerCase().includes(normalizedFilter)
      );
    };

    (listings || []).forEach(listing => {
      if (!matchesFilter(listing)) return;
      const brand = getBrandDisplayName(listing?.attributes?.publicData?.brand);
      if (!byBrand.has(brand)) {
        byBrand.set(brand, []);
      }
      byBrand.get(brand).push(listing);
    });

    return Array.from(byBrand.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [listings, normalizedFilter, config]);

  const handleSelect = listingId => {
    onSelect?.(listingId);
    setIsOpen(false);
  };

  return (
    <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
      <div className={css.dropdown}>
        <label className={css.label} htmlFor={id}>
          {label}
        </label>
        <div className={css.dropdownButtonRow}>
          <button
            id={id}
            type="button"
            className={css.dropdownButton}
            onClick={() => setIsOpen(prev => !prev)}
          >
            <span className={css.dropdownButtonText}>
              {selectedListing ? optionLabel(selectedListing, intl) : 'Sélectionner un produit'}
            </span>
            <span className={classNames(css.caret, { [css.caretOpen]: isOpen })} />
          </button>
          {selectedListing ? (
            <button
              type="button"
              className={css.clearButton}
              onClick={() => onSelect?.('')}
              aria-label="Effacer la sélection"
            >
              ✕
            </button>
          ) : null}
        </div>
        {isOpen ? (
          <div className={css.dropdownPanel}>
            <input
              className={css.dropdownSearch}
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Rechercher un produit"
            />
            <div className={css.dropdownList}>
              {groupedListings.length === 0 ? (
                <div className={css.emptyState}>Aucun produit trouvé</div>
              ) : (
                groupedListings.map(([brand, items]) => (
                  <div key={brand} className={css.group}>
                    <div className={css.groupLabel}>{brand}</div>
                    {items.map(listing => {
                      const idValue = listing?.id?.uuid;
                      const price = listing?.attributes?.price;
                      const image =
                        listing.images && listing.images.length > 0 ? listing.images[0] : null;
                      const variants = image
                        ? Object.keys(image?.attributes?.variants || {}).filter(k =>
                            k.startsWith('listing-card')
                          )
                        : [];
                      const isDisabled = idValue === disabledId;
                      const isSelected = idValue === selectedId;
                      return (
                        <button
                          key={idValue}
                          type="button"
                          className={classNames(css.option, {
                            [css.optionDisabled]: isDisabled,
                            [css.optionSelected]: isSelected,
                          })}
                          onClick={() => !isDisabled && handleSelect(idValue)}
                        >
                          <span className={css.optionTitle}>
                            {image ? (
                              <span className={css.optionThumbnail}>
                                <ResponsiveImage
                                  className={css.optionThumbnailImage}
                                  alt={listing?.attributes?.title || ''}
                                  image={image}
                                  variants={variants}
                                  sizes="50px"
                                />
                              </span>
                            ) : null}
                            <span className={css.optionTitleText}>
                              {listing?.attributes?.title || ''}
                            </span>
                          </span>
                          <span className={css.optionMeta}>
                            {price ? formatMoney(intl, price) : '—'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        ) : null}
      </div>
    </OutsideClickHandler>
  );
};

const ComparePicker = props => {
  const {
    className,
    listings = [],
    secondListings,
    firstListingId,
    secondListingId,
    onChange,
    labelPrefix,
  } = props;
  const classes = classNames(css.root, className);
  const labelBase = labelPrefix || 'Produit';
  const resolvedSecondListings = secondListings || listings;

  const handleFirstChange = listingId => {
    onChange?.({ firstListingId: listingId, secondListingId });
  };

  const handleSecondChange = listingId => {
    onChange?.({ firstListingId, secondListingId: listingId });
  };

  return (
    <div className={classes}>
      <CompareDropdown
        id="compare-first"
        label={`${labelBase} 1`}
        listings={listings}
        selectedId={firstListingId}
        disabledId={secondListingId}
        onSelect={handleFirstChange}
      />
      <CompareDropdown
        id="compare-second"
        label={`${labelBase} 2`}
        listings={resolvedSecondListings}
        selectedId={secondListingId}
        disabledId={firstListingId}
        onSelect={handleSecondChange}
      />
    </div>
  );
};

export default ComparePicker;
